import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import SaveButton from '../../../components/Buttons/SaveButton';
import ButtonBar from '../../../components/Fields/ButtonBar';
import DateControl from '../../../components/Fields/DateControl';
import Field from '../../../components/Fields/Field';
import FieldLabel from '../../../components/Fields/FieldLabel';
import Form from '../../../components/Fields/Form';
import SelectListField from '../../../components/Fields/SelectListField';
import SwitchField from '../../../components/Fields/SwitchField';
import TextareaField from '../../../components/Fields/TextareaField';
import TextboxField from '../../../components/Fields/TextboxField';
import { FieldConfig, FormConfig } from '../../../components/Fields/Types';
import UserDefinedField from '../../../components/Fields/UserDefinedField';
import useSelectListOptions from '../../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from "../../../components/Spinner/SpinnerOverlay";
import { ApplicationFunctions, useAuth } from '../../../contexts/authContext';
import { useNotifications } from '../../../contexts/notificationContext';
import { BusinessPartnerTypeEnum } from '../../../enums/BusinessPartnerTypeEnum';
import UserDefinedFieldValueModel from '../../../models/UserDefinedFieldValueModel';
import DataService from '../../../services/data.service';
import { Result } from '../../../types/Result';
import FormatUtils from '../../../utils/FormatUtils';
import Unauthorized from '../../Authentication/Unauthorized';

interface LicenseCreateModel {
    referenceCode?: string
    name: string
    licensorBusinessPartnerId?: number
    beginDate: string
    endDate?: string
    summary?: string
    sampleRemarks?: string
    sellOffPeriod?: number
    isValid: boolean

    userDefinedFields: UserDefinedFieldValueModel[]
}

interface IProps {
    onLicenseCreated: (licenseId: number) => void
}

const CreateLicenseView: React.FC<IProps> = ({ onLicenseCreated }) => {
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] });
    const { handleResult, handleException } = useNotifications();
    const [showSpinner, setShowSpinner] = useState(false);

    const [formData, setFormData] = useState<LicenseCreateModel>({
        referenceCode: '',
        name: '',
        licensorBusinessPartnerId: 1,
        beginDate: new FormatUtils().formatDateForStorage(new Date()),
        endDate: '',
        summary: '',
        sampleRemarks: '',
        isValid: false,
        userDefinedFields: []
    });

    const licensors = useSelectListOptions('api/v1/selectlist/BusinessPartners?activeOnly=true&businessPartnerTypeId=' + BusinessPartnerTypeEnum.Licensor, '-- Select licensor')
    const divisions = useSelectListOptions('api/v1/selectlist/Divisions?activeOnly=true', '-- Select division');

    useEffect(() => {
        setShowSpinner(true);
        DataService.get<UserDefinedFieldValueModel[]>('api/v1/licenseUserDefinedFields/ForNewLicense/')
            .then(response => {
                setShowSpinner(false);
                formData.userDefinedFields = [...response];
                setFormData({ ...formData });
            })
            .catch(e => handleException(e));
    }, [])

    function submit() {
        setShowSpinner(true);
        if (formData) {
            DataService
                .create<number>(`api/v1/Licenses/`, formData)
                .then(response => {
                    setShowSpinner(false);
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        onLicenseCreated(response.data);
                })
                .catch(e => {
                    setShowSpinner(false);
                    handleException(e)
                });
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'referenceCode', label: 'Referencecode', labelSize: 3 },
            { name: 'name', label: 'Name', labelSize: 3, validationRules: [{ type: 'isRequired' }] },
            { name: 'licensorBusinessPartnerId', label: 'Licensor', labelSize: 3 },
            { name: 'licenseeDivisionId', label: 'Licensee', labelSize: 3 },
            { name: 'beginDate', label: 'Active from', labelSize: 3, validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
            { name: 'endDate', label: 'Until', labelSize: 'auto', validationRules: [{ type: 'isDate' }] },
            { name: 'isValid', label: 'Valid', labelSize: 3 },
            { name: 'summary', label: 'Summary', labelSize: 3 },
            { name: 'sampleRemarks', label: 'Sample remarks', labelSize: 4 },
            { name: 'sellOffPeriod', label: 'Sell-off period (months)', labelSize: 4 },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    if (formData && formData.userDefinedFields) {
        formData.userDefinedFields.forEach((value, index) => {
            let label = value.userDefinedField.name
            formConfig.fields.push({ name: 'userDefinedFields.' + index + '.value', label: label, labelSize: 4 });
        });
    }

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);
    const { isAuthorizedForProjectType } = useAuth();

    if (!isAuthorizedForProjectType(ApplicationFunctions.CreateLicense)) {
        return (<Unauthorized />);
    }

    return (
        <div className='main-content'>
            <Form
                title={'Create new license'}
                footer={(
                    <Row>
                        <Col md='5'>
                            <ButtonBar offset={3}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </ButtonBar>
                        </Col>
                        <Col md='7' className='hidden-md'>
                            <ButtonBar offset={0}>
                            </ButtonBar>
                        </Col>
                    </Row>
                )}>
                <Container fluid>
                    <Row>
                        <Col md='6'>
                            <TextboxField {...getFieldProps('referenceCode')} />
                            <TextboxField {...getFieldProps('name')} />
                            <SelectListField {...getFieldProps('licensorBusinessPartnerId')} options={licensors} />
                            <SelectListField {...getFieldProps('licenseeDivisionId')} options={divisions} />
                            <Field {...getLabelProps('beginDate')}>
                                <DateControl {...getControlProps('beginDate')} />
                                <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                                <DateControl {...getControlProps('endDate')} />
                            </Field>
                            <SwitchField {...getFieldProps('isValid')} />
                            <TextareaField {...getFieldProps('summary')} />
                        </Col>
                        <Col md='6'>
                            <TextboxField {...getFieldProps('sampleRemarks')} />
                            <TextboxField {...getFieldProps('sellOffPeriod')} />
                            {formData && formData.userDefinedFields &&
                                formData.userDefinedFields.map((value, index) => {
                                    let name = 'userDefinedFields.' + index + '.value';
                                    return (<UserDefinedField {...getFieldProps(name)} userDefinedFieldValue={value} key={'userDefinedField' + index} />);
                                })
                            }
                        </Col>
                    </Row>
                </Container>
            </Form>
            {showSpinner && <SpinnerOverlay />}
        </div>
    );
}
export default CreateLicenseView;